import React from 'react';
import { withFormik, FieldArray } from 'formik';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { get, isNil } from 'lodash';
// components
import Modal from 'shared/modals/Modal';
import { FormikInput, FormikSelect, FormikToggle, FormikTextarea } from 'shared/forms';
import LoadingModal from 'shared/loading/LoadingModal';
import Icon from 'shared/icons/IconComponent';

const ContactFormModal = ({
  isOpen,
  close,
  contactTypes,
  terminals,
  setFieldValue,
  setFieldTouched,
  values,
  handleChange,
  handleBlur,
  handleSubmit,
  setSubmitting,
  touched,
  errors,
  isSubmitting,
  disabled,
  initialValues,
  resetForm,
  organizationId,
  selectedRoles
}) => {
  const isEditing = initialValues && initialValues.id;
  const reset = () => {
    close();
    resetForm();
  };

  return (
    <Modal isOpen={isOpen} handleClose={reset} containerStyle='large'>
      <div className='modal-header'>
        <h5 className='modal-title' id='addAdditionalChargeModal'>
          {isEditing ? 'Edit' : 'Create'} Contact
        </h5>
        <button type='button' className='button large close' onClick={reset} />
      </div>
      <section className={`additional-charges-form modal-body ${isSubmitting ? 'hide-overflow' : ''}`}>
        <LoadingModal isOpen={isSubmitting} isNested={true} />
        <form onSubmit={handleSubmit}>
          <section className='formik-form rows'>
            <div className='formik-row'>
              <FormikInput
                wrapperClass='wrapper-two-column'
                type='text'
                label='First Name'
                name='firstName'
                required
                value={values.firstName}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
                disabled={disabled}
              />
              <FormikInput
                wrapperClass='wrapper-two-column'
                type='text'
                label='Last Name'
                name='lastName'
                value={values.lastName}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
                disabled={disabled}
              />
            </div>
            <div className='formik-row'>
              <FormikSelect
                wrapperClass='wrapper-three-column'
                onChange={selected => setFieldValue('customerContactTypeId', selected.value)}
                label='Contact Type'
                options={contactTypes
                  .filter(type => isNil(type.roleType) || selectedRoles?.includes(type.roleType))
                  .map(x => ({ label: x.name, value: x.id }))}
                name='customerContactTypeId'
                required
                value={values.customerContactTypeId}
                blurInputOnSelect
                onBlur={setFieldTouched}
                touched={touched}
                errors={errors}
                disabled={disabled}
              />
              <FormikInput
                wrapperClass='wrapper-three-column'
                type='text'
                label='Title'
                name='title'
                value={values.title}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
                disabled={disabled}
              />
              <FormikSelect
                wrapperClass='wrapper-two-column'
                label='Market'
                options={terminals?.map(({ abbreviation, id }) => ({ label: abbreviation, value: id }))}
                name='terminalId'
                isClearable={true}
                value={values.terminalId}
                onChange={selected => {
                  setFieldValue('terminalId', selected?.value);
                  setFieldValue('terminalName', selected?.label);
                }}
                blurInputOnSelect
                onBlur={setFieldTouched}
                touched={touched}
                errors={errors}
                disabled={disabled}
              />
            </div>
            <div className='formik-row'>
              <FormikTextarea
                wrapperClass='wrapper-one-column'
                type='text'
                label='Comment'
                name='comment'
                style={{ minHeight: 50 }}
                value={values.comment}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
                disabled={disabled}
              />
            </div>
            <div className='row'>
              <div className='col col--lg-6'>
                <div className='formik-column'>
                  <div className='formik-wrapper'>
                    <label className='title'>Emails</label>
                    <FieldArray
                      name='emailAddresses'
                      render={({ remove, replace, push }) => (
                        <div>
                          {values.emailAddresses.map((e, index) =>
                            !e.deleted ? (
                              <div key={index} className='formik-row' style={{ alignItems: 'flex-end' }}>
                                <FormikInput
                                  type='text'
                                  name={`emailAddresses.${index}.email`}
                                  value={e.email}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  touched={touched}
                                  errors={errors}
                                  touchedNested={get(touched, ['emailAddresses', index, 'email'])}
                                  errorMessage={get(errors, ['emailAddresses', index, 'email'])}
                                  disabled={disabled}
                                />
                                <FormikToggle
                                  wrapperClass='wrapper-small-static-column margin--none'
                                  label='Invoice?'
                                  name={`emailAddresses.${index}.invoiceContact`}
                                  value={e.invoiceContact}
                                  onToggle={value => setFieldValue(`emailAddresses.${index}.invoiceContact`, value)}
                                  touched={touched}
                                  errors={errors}
                                  onTouch={() => setFieldTouched(`emailAddresses.${index}.invoiceContact`, true)}
                                  onText='Yes'
                                  offText='No'
                                />
                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '.25rem' }}>
                                  <div style={{ marginTop: 0 }}>
                                    <button
                                      type='button'
                                      disabled={disabled}
                                      className='button smallcolor--secondary-color label--margin-top flex col--center'
                                    >
                                      <a href={`mailto:${e.email}`}>
                                        <Icon icon='envelope' position='none' className='icon--font-medium' />
                                      </a>
                                    </button>
                                  </div>
                                  <div style={{ marginTop: 0 }}>
                                    <button
                                      type='button'
                                      disabled={disabled}
                                      className='button smallcolor--error-color label--margin-top flex col--center'
                                      onClick={() => {
                                        if (e.id) {
                                          replace(index, { ...e, deleted: true });
                                          if (values.emailAddresses.filter(email => !email.deleted).length <= 1) {
                                            push({ email: '', deleted: false });
                                          }
                                        } else {
                                          if ((index === 0 && values.emailAddresses.length > 1) || index > 0) {
                                            return remove(index);
                                          }
                                          return replace(index, { email: '', deleted: false });
                                        }
                                      }}
                                    >
                                      <Icon icon='times-circle' position='none' className='icon--font-medium' />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ) : null
                          )}
                        </div>
                      )}
                    />
                    <section className='toolbar toolbar--xxs'>
                      <div className='toolbar--column toolbar--left'>
                        <button
                          type='button'
                          className='button large primary text-align--center toolbar--button--margin'
                          disabled={isSubmitting || values.emailAddresses.some(({ email }) => !email)}
                          onClick={() => setFieldValue('emailAddresses', [...values.emailAddresses, { email: '', deleted: false }])}
                        >
                          <Icon icon='plus-circle' position='left' className='icon--adjust-top font-size--large' />
                          Add Email
                        </button>
                      </div>
                    </section>
                  </div>
                </div>
              </div>
              <div className='col col--lg-6'>
                <div className='formik-column'>
                  <div className='formik-wrapper'>
                    <label className='title'>Phone Numbers</label>
                    <FieldArray
                      name='phoneNumbers'
                      render={({ remove, replace, push }) => (
                        <div>
                          {values.phoneNumbers.map((p, index) =>
                            !p.deleted ? (
                              <div key={index} className='formik-row'>
                                <FormikInput
                                  type='text'
                                  name={`phoneNumbers.${index}.phoneNumber`}
                                  value={p.phoneNumber}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  touched={touched}
                                  errors={errors}
                                  touchedNested={get(touched, ['phoneNumbers', index, 'phoneNumber'])}
                                  errorMessage={get(errors, ['phoneNumbers', index, 'phoneNumber'])}
                                  disabled={disabled}
                                  customInput='phone'
                                />
                                <button
                                  type='button'
                                  disabled={disabled}
                                  className='button smallcolor--secondary-color label--margin-top flex col--center'
                                >
                                  <a href={`tel:${p.phoneNumber}`}>
                                    <Icon icon='phone' position='none' className='icon--font-medium' />
                                  </a>
                                </button>
                                <button
                                  type='button'
                                  disabled={disabled}
                                  className='button smallcolor--error-color label--margin-top flex col--center'
                                  onClick={() => {
                                    if (p.id) {
                                      replace(index, { ...p, deleted: true });
                                      if (values.phoneNumbers.filter(phone => !phone.deleted).length <= 1) {
                                        push({ phoneNumber: '', deleted: false });
                                      }
                                      return false;
                                    } else {
                                      if ((index === 0 && values.phoneNumbers.length > 1) || index > 0) {
                                        return remove(index);
                                      }
                                      return replace(index, { phoneNumber: '', deleted: false });
                                    }
                                  }}
                                >
                                  <Icon icon='times-circle' position='none' className='icon--font-medium' />
                                </button>
                              </div>
                            ) : null
                          )}
                        </div>
                      )}
                    />
                    <section className='toolbar toolbar--xxs'>
                      <div className='toolbar--column toolbar--left'>
                        <button
                          type='button'
                          className='button large primary text-align--center toolbar--button--margin'
                          disabled={isSubmitting || values.phoneNumbers.some(({ phoneNumber }) => !phoneNumber)}
                          onClick={() => setFieldValue('phoneNumbers', [...values.phoneNumbers, { phoneNumber: '', deleted: false }])}
                        >
                          <Icon icon='plus-circle' position='left' className='icon--adjust-top font-size--large' />
                          Add Phone Number
                        </button>
                      </div>
                    </section>
                  </div>
                </div>
              </div>
            </div>
          </section>
          <section className='toolbar toolbar--xxs'>
            <div className='toolbar--column toolbar--right'>
              <button
                type='button'
                className='button large primary text-align--center toolbar--button--margin'
                disabled={isSubmitting}
                onClick={handleSubmit}
              >
                {values.id ? 'Update' : 'Add'}
              </button>
              {initialValues && initialValues.id && (
                <button
                  type='button'
                  className='button large error-color text-align--center toolbar--button--margin'
                  disabled={isSubmitting}
                  onClick={() => {
                    setSubmitting(true);
                    // remove any empty emails and phone numbers that were not already created
                    values.emailAddresses = values.emailAddresses.filter(e => e.email || e.id);
                    values.phoneNumbers = values.phoneNumbers.filter(p => p.phoneNumber || p.id);

                    close({ organizationId, ...values, deleted: true });
                    resetForm();
                  }}
                >
                  Delete
                </button>
              )}
              <button
                id='add-additional-charge-cancel'
                type='button'
                className='button large white text-align--center margin--none--xxs'
                onClick={() => reset()}
              >
                Cancel
              </button>
            </div>
          </section>
        </form>
      </section>
    </Modal>
  );
};

ContactFormModal.propTypes = {
  isOpen: PropTypes.bool,
  close: PropTypes.func,
  contactTypes: PropTypes.array,
  values: PropTypes.object,
  handleChange: PropTypes.func,
  handleBlur: PropTypes.func,
  handleSubmit: PropTypes.func,
  touched: PropTypes.object,
  errors: PropTypes.object,
  isSubmitting: PropTypes.bool,
  initialValues: PropTypes.object,
  setFieldValue: PropTypes.func,
  setFieldTouched: PropTypes.func,
  resetForm: PropTypes.func,
  disabled: PropTypes.bool
};

const ContactSchema = Yup.object().shape({
  customerContactTypeId: Yup.string().required('Contact Type is Required'),
  firstName: Yup.string().required('First Name is Required'),
  lastName: Yup.string(),
  title: Yup.string(),
  terminalId: Yup.number().nullable(),
  deleted: Yup.boolean().default(false),
  emailAddresses: Yup.array().of(
    Yup.object().shape({
      id: Yup.string(),
      email: Yup.string().email('Please enter a valid email'),
      deleted: Yup.boolean().default(false)
    })
  ),
  phoneNumbers: Yup.array().of(
    Yup.object().shape({
      id: Yup.string(),
      phoneNumber: Yup.string()
        .nullable()
        .trim()
        .test('phone 14 chars', 'Not a valid Phone Number', phone => phone?.length === 14)
        .required('Phone Number is Required'),
      deleted: Yup.boolean().default(false)
    })
  )
});

export default withFormik({
  enableReinitialize: true,
  mapPropsToValues: ({ initialValues }) => {
    const emails = (get(initialValues, 'emailAddresses') || []).filter(e => !e.deleted);
    const numbers = (get(initialValues, 'phoneNumbers') || []).filter(p => !p.deleted);
    return initialValues
      ? {
          ...initialValues,
          ...(emails && emails.length === 0 && { emailAddresses: [{ email: '', deleted: false }] }),
          ...(numbers && numbers.length === 0 && { phoneNumbers: [{ phoneNumber: '', deleted: false }] })
        }
      : {
          customerContactTypeId: '',
          firstName: '',
          lastName: '',
          title: '',
          emailAddresses: [{ email: '', deleted: false }],
          phoneNumbers: [{ phoneNumber: '', deleted: false }]
        };
  },
  validationSchema: ContactSchema,
  handleSubmit: async (values, { setSubmitting, resetForm, props: { close, organizationId } }) => {
    setSubmitting(true);
    // remove any empty emails and phone numbers that were not already created
    values.emailAddresses = values.emailAddresses.filter(e => e.email || e.id);
    values.phoneNumbers = values.phoneNumbers.filter(p => p.phoneNumber || p.id);

    close({ organizationId, ...values });
    resetForm();
  }
})(ContactFormModal);
